import { useTranslation } from 'react-i18next';
import { RankPatentBadge } from '@/components/ranks/RankPatentBadge';
import { trackLadderRows } from '@/lib/ranks/trackLadderRows';
import { cn } from '@/lib/utils';
import type { ClipperRankTrackId } from '@/generated/graphql';

export type RankTierStripTrack = {
  id: ClipperRankTrackId;
  current_tier_id?: string | null;
  unlocked_tier_ids: string[];
};

/**
 * Every tier of a track as one compact left-to-right progression rail.
 *
 * The card column is ~200px wide, so tier names cannot be shown here without
 * truncating them ("Alcance ..."). Names live in each badge's tooltip and, in
 * full, in the ladder dialog; the strip only has to answer "how many levels
 * are there, and where am I".
 */
export function RankTierStrip({ track }: { track: RankTierStripTrack }) {
  const { t } = useTranslation();
  // trackLadderRows is highest-first for the ladder; a horizontal rail reads
  // as progression only when it runs lowest-first.
  const rows = trackLadderRows({
    trackId: track.id,
    currentTierId: track.current_tier_id,
    unlockedTierIds: track.unlocked_tier_ids,
  }).slice().reverse();

  if (rows.length === 0) return null;

  return (
    <ol
      data-testid="clipper-ranks-tier-strip"
      aria-label={t('ranks.ladder.stripLabel')}
      className="flex items-center py-0.5"
    >
      {rows.map((row, index) => {
        const next = rows[index + 1];
        return (
          <li
            key={row.tierId}
            data-tier-id={row.tierId}
            data-state={row.current ? 'current' : row.earned ? 'earned' : 'locked'}
            className={cn('flex items-center', index < rows.length - 1 && 'min-w-0 flex-1')}
          >
            <RankPatentBadge
              tierId={row.tierId}
              size="sm"
              showLabel={false}
              earned={row.earned}
              className={cn(
                'rounded-full',
                row.current && 'ring-2 ring-primary ring-offset-1 ring-offset-card',
                !row.earned && 'opacity-60',
              )}
            />
            {next ? (
              <span
                aria-hidden="true"
                className={cn(
                  'mx-0.5 h-0.5 min-w-1 flex-1 rounded-full',
                  next.earned ? 'bg-primary/70' : 'bg-border',
                )}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
